import React, { useState, useEffect, useCallback } from 'react';
import {
  FaCalendarAlt, FaDownload, FaCheckCircle, FaTimesCircle, FaClock,
  FaPlaneDeparture, FaHourglassHalf, FaChevronLeft, FaChevronRight,
} from 'react-icons/fa';
import api, { downloadExport, errorMessage, getUser } from '../api';
import {
  PageShell, Card, StatCard, Badge, Button, Alert, Loading, RateBar, Table,
  formatTime, MONTHS, STATUS_LABELS,
} from './attendance/ui';

const STATUS_DOT = {
  present: 'bg-green-500',
  late: 'bg-amber-500',
  half_day: 'bg-orange-400',
  absent: 'bg-red-500',
  on_leave: 'bg-purple-500',
  holiday: 'bg-blue-400',
  weekend: 'bg-gray-200',
  no_record: 'bg-gray-200',
};

/** A staff member's own month: calendar grid, day list and totals. */
const MonthlyReport = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const user = getUser();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('attendance/calendar/', { params: { month, year } });
      setReport(data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load your monthly report.'));
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const shift = (delta) => {
    const next = new Date(year, month - 1 + delta, 1);
    setMonth(next.getMonth() + 1);
    setYear(next.getFullYear());
  };

  const exportReport = async (format) => {
    try {
      await downloadExport(
        { month, year, employee: user?.employeeId, type: 'detailed', format },
        `my_attendance_${year}_${String(month).padStart(2, '0')}.${format === 'xlsx' ? 'xlsx' : 'csv'}`,
      );
    } catch (err) {
      setError(errorMessage(err, 'Export failed.'));
    }
  };

  const summary = report?.summary;
  const days = report?.days || [];
  // Pad the grid so the first of the month lands on the right weekday (Mon-first).
  const leadingBlanks = days.length
    ? (new Date(`${days[0].date}T00:00:00`).getDay() + 6) % 7
    : 0;

  return (
    <PageShell
      title="My Monthly Report"
      subtitle={`${user?.name || ''}${user?.employeeId ? ` · ${user.employeeId}` : ''}`}
      actions={
        <>
          <Button variant="secondary" onClick={() => exportReport('csv')}><FaDownload /> CSV</Button>
          <Button variant="secondary" onClick={() => exportReport('xlsx')}><FaDownload /> Excel</Button>
        </>
      }
    >
      <Alert>{error}</Alert>

      <Card className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => shift(-1)} className="p-2.5 rounded-xl hover:bg-gray-100 text-gray-600 dark:hover:bg-slate-800 dark:text-slate-200" aria-label="Previous month">
              <FaChevronLeft />
            </button>
            <div className="text-xl font-bold text-gray-800 min-w-[180px] text-center dark:text-slate-200">
              {MONTHS[month - 1]} {year}
            </div>
            <button onClick={() => shift(1)} className="p-2.5 rounded-xl hover:bg-gray-100 text-gray-600 dark:hover:bg-slate-800 dark:text-slate-200" aria-label="Next month">
              <FaChevronRight />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-[#15A857] dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200">
              {MONTHS.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-[#15A857] dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200">
              {Array.from({ length: 6 }, (_, index) => now.getFullYear() - index)
                .map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <StatCard icon={<FaCalendarAlt />} label="Working days" tone="gray" value={summary?.totalWorkingDays ?? 0} />
            <StatCard icon={<FaCheckCircle />} label="Days present" tone="green" value={summary?.daysPresent ?? 0} />
            <StatCard icon={<FaTimesCircle />} label="Days absent" tone="red" value={summary?.daysAbsent ?? 0} />
            <StatCard icon={<FaClock />} label="Late days" tone="amber" value={summary?.lateDays ?? 0} />
            <StatCard icon={<FaPlaneDeparture />} label="Leave days" tone="purple" value={summary?.leaveDays ?? 0} />
            <StatCard icon={<FaHourglassHalf />} label="Hours worked" tone="blue"
              value={summary?.totalWorkingHours ?? 0} hint={`+${summary?.overtimeHours ?? 0}h overtime`} />
          </div>

          <Card title="Attendance rate" className="mb-6">
            <RateBar value={summary?.attendancePercentage ?? 0} />
            <p className="text-sm text-gray-500 mt-3">
              {summary?.daysPresent ?? 0} day(s) attended out of {summary?.totalWorkingDays ?? 0}{' '}
              expected working day(s) this month.
            </p>
          </Card>

          <Card title="Calendar" className="mb-6">
            <div className="grid grid-cols-7 gap-2 mb-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label) => <div key={label}>{label}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: leadingBlanks }).map((_, index) => <div key={`pad-${index}`} />)}
              {days.map((day) => (
                <div key={day.date}
                  title={`${day.date} · ${STATUS_LABELS[day.status] || day.status}`}
                  className="aspect-square rounded-xl border border-gray-100 bg-white p-1.5 flex flex-col items-center justify-center gap-1 hover:border-[#15A857] transition-colors dark:border-slate-700 dark:bg-slate-900/70 dark:hover:border-[#15A857]">
                  <span className="text-sm font-semibold text-gray-700 dark:text-slate-200">
                    {Number(day.date.slice(8, 10))}
                  </span>
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[day.status] || 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 mt-5 pt-4 border-t border-gray-200 text-xs text-gray-600 dark:border-slate-700 dark:text-slate-300">
              {Object.entries(STATUS_DOT).map(([status, colour]) => (
                <span key={status} className="flex items-center gap-1.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${colour}`} />
                  {STATUS_LABELS[status] || status}
                </span>
              ))}
            </div>
          </Card>

          <Card title="Day by day">
            <Table
              columns={['Date', 'Check in', 'Check out', 'Hours', 'Late', 'Overtime', 'Status']}
              rows={days.filter((day) => !['weekend', 'holiday'].includes(day.status))}
              empty="No recorded days this month."
              renderRow={(day) => (
                <tr key={day.date} className="border-b last:border-0 hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-800/70">
                  <td className="py-3 pr-4 font-medium text-gray-800 whitespace-nowrap dark:text-slate-200">{day.date}</td>
                  <td className="py-3 pr-4 dark:text-slate-300">{formatTime(day.checkIn)}</td>
                  <td className="py-3 pr-4 dark:text-slate-300">{formatTime(day.checkOut)}</td>
                  <td className="py-3 pr-4 dark:text-slate-300">{day.workingHours ? `${day.workingHours}h` : '—'}</td>
                  <td className="py-3 pr-4 dark:text-slate-300">{day.lateMinutes ? `${day.lateMinutes} min` : '—'}</td>
                  <td className="py-3 pr-4 dark:text-slate-300">{day.overtimeHours ? `${day.overtimeHours}h` : '—'}</td>
                  <td className="py-3 pr-4"><Badge value={day.status} /></td>
                </tr>
              )}
            />
          </Card>
        </>
      )}
    </PageShell>
  );
};

export default MonthlyReport;
